import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';

// Styles
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  MenuIconButton,
  StyledMenu,
} from 'js/components/Configurations/Campaigns/ListCampaigns/styles';
import { DiceThree, DotsThreeVertical, ListChecks, PencilSimple, Trash } from 'phosphor-react';

// Components
import { FullscreenLoader } from 'js/components/TriiboComponents/FullScreenLoader/FullScreenLoader';
import { ModalDeleteDraw } from './ModalDeleteDraw';
import usePagination from 'js/components/TriiboComponents/Tables/usePagination';

// Apis
import { generateUsersListForDraw } from 'js/library/utils/API/Draws/generateUsersListForDraw';

export const DrawTable = ({ drawList, loadingTable, reload }) => {
  const history = useHistory();
  const { id } = useParams();

  const PER_PAGE = 10;
  const _DATA = usePagination(drawList, PER_PAGE);
  const count = Math.ceil(drawList.length / PER_PAGE);

  const [menus, setMenus] = useState({});
  const [loadingGenerateList, setLoadingGenerateList] = useState(false);
  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);
  const [raffleIdentifierSelected, setRaffleIdentifierSelected] = useState(null);

  const generateList = async (drawId) => {
    setLoadingGenerateList(true);
    try {
      await generateUsersListForDraw(drawId);
      toast.success('A lista está em processamento. Seu sorteio estará disponível em instantes!');
      reload((prev) => !prev);
    } catch (error) {
      toast.error(error);
    } finally {
      setLoadingGenerateList(false);
    }
  };

  const handleChangePage = (e, p) => {
    _DATA.jump(p);
  };

  const handleClick = (event, drawId) => {
    setMenus((prevMenus) => ({
      ...prevMenus,
      [drawId]: event.currentTarget,
    }));
  };

  const handleClose = (drawId) => {
    setMenus((prevMenus) => ({
      ...prevMenus,
      [drawId]: null,
    }));
  };

  const handleModalDelete = (raffleIdentifier) => {
    raffleIdentifier && setRaffleIdentifierSelected(raffleIdentifier);
    setIsOpenModalDelete(!isOpenModalDelete);
  };

  const redirectDraw = (event, selected, type) => {
    event.preventDefault();

    const route = type === 'editar' ? 'editar' : 'sorteio';

    history.push({
      pathname: `/admin/seu-clube/sorteios/${id}/${route}`,
      state: {
        drawSettings: selected,
      },
    });
  };

  if (loadingTable) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={40} thickness={5} />
      </div>
    );
  }

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} size="medium">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                <strong>Nome do sorteio</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Campanha</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Período</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Quantidade de sorteios</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Lista gerada/Em processamento</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Ação</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {_DATA.currentData().map((draw, index) => {
              return (
                <TableRow key={draw.id}>
                  <TableCell align="center">{draw.raffleName || 'Não consta'}</TableCell>
                  <TableCell align="center">{draw.campaignId}</TableCell>
                  <TableCell align="center">{draw.periodId}</TableCell>
                  <TableCell align="center">{draw.raffleQuantity}</TableCell>
                  <TableCell align="center">
                    {draw.hasOwnProperty('hasUsersList') ? 'Sim' : 'Não'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      id={`menu-button-${draw.id}`}
                      aria-controls={menus[draw.id] ? `menu-${draw.id}` : undefined}
                      aria-haspopup="true"
                      onClick={(event) => handleClick(event, draw.id)}
                      sx={{
                        backgroundColor: '#ebe6ea',
                        borderRadius: '50px',
                        color: 'gray',
                        '&:hover': { color: '#6e3296' },
                      }}
                    >
                      <DotsThreeVertical size={28} weight="bold" />
                    </IconButton>
                    <StyledMenu
                      id={`menu-${draw.id}`}
                      anchorEl={menus[draw.id]}
                      open={Boolean(menus[draw.id])}
                      onClose={() => handleClose(draw.id)}
                      MenuListProps={{
                        'aria-labelledby': `menu-button-${draw.id}`,
                      }}
                    >
                      {!draw.hasOwnProperty('hasUsersList') ? (
                        <MenuItem onClick={handleClose} disableRipple>
                          <MenuIconButton
                            to="#"
                            onClick={() => {
                              generateList(draw.id);
                              handleClose(draw.id);
                            }}
                          >
                            <ListChecks size={20} />
                            Gerar lista
                          </MenuIconButton>
                        </MenuItem>
                      ) : (
                        <MenuItem onClick={handleClose} disableRipple>
                          <MenuIconButton
                            to="#"
                            onClick={(event) => redirectDraw(event, draw, 'sorteio')}
                          >
                            <DiceThree size={20} weight="bold" /> Sortear/Ver sorteio
                          </MenuIconButton>
                        </MenuItem>
                      )}
                      <MenuItem onClick={handleClose} disableRipple>
                        <MenuIconButton
                          to="#"
                          onClick={(event) => redirectDraw(event, draw, 'editar')}
                        >
                          <PencilSimple size={20} weight="bold" />
                          Editar
                        </MenuIconButton>
                      </MenuItem>

                      <MenuItem onClick={handleClose} disableRipple>
                        <MenuIconButton to="#" onClick={() => handleModalDelete(draw.id)}>
                          <Trash size={20} weight="bold" />
                          Deletar
                        </MenuIconButton>
                      </MenuItem>
                    </StyledMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <ModalDeleteDraw
          open={isOpenModalDelete}
          close={handleModalDelete}
          raffleIdentifier={raffleIdentifierSelected}
          reload={reload}
        />
      </TableContainer>
      <Pagination count={count} onChange={handleChangePage} />
      {loadingGenerateList && <FullscreenLoader />}
    </>
  );
};
